// =============================================================================
// features/sales/components/ScheduleAppointmentDialog.tsx — Diálogo de
// agendamento REUTILIZÁVEL (doc 26 §7 `ScheduleAppointmentDialog`).
//
// Autocontido: lead (fixo via prop — quem abre já sabe qual lead), data,
// hora, responsável, canal, motivo e observação. F33-S07 (CRM) e F33-S08
// (Conversas) importam este componente de `features/sales/index.ts` em vez
// de recriar o formulário — contrato de saída do slot F33-S06.
//
// DS: painel `--elev-5`, overlay semitransparente, animação fade-up,
// foco preso (useFocusTrap) com devolução ao fechar, Escape fecha só sem
// edição pendente (guarda — doc 26 §7 "ações destrutivas/perda de dados").
//
// Erro de rede NUNCA limpa o formulário (doc 26 §7 "falha de telefonia não
// apaga o resultado comercial já preenchido" — mesmo princípio vale para
// falha de rede ao agendar): o estado do react-hook-form só é tocado em
// sucesso; em erro, mostramos o Alert e deixamos os campos como estavam
// para o usuário tentar de novo.
// =============================================================================

import type { AppointmentCreateBody } from '@elemento/shared-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../../components/ui/Button';
import { useAssignableAgents } from '../../../hooks/admin/useAgents';
import { cn } from '../../../lib/cn';
import { useCreateAppointment } from '../agenda/hooks';

import { defaultTimeOrgTz, orgLocalToUtcIso } from './dateTimeOrgTz';
import { ScheduleAppointmentFields } from './ScheduleAppointmentFields';
import { useFocusTrap } from './useFocusTrap';

const ScheduleFormSchema = z.object({
  date: z.string().min(1, 'Informe a data'),
  time: z.string().min(1, 'Informe a hora'),
  durationMinutes: z.string(),
  assignedAgentId: z.string(),
  channel: z.string().min(1, 'Selecione o canal'),
  reason: z.string().max(500, 'Máximo 500 caracteres').optional(),
  notes: z.string().max(4000, 'Máximo 4000 caracteres').optional(),
});

export type ScheduleFormValues = z.infer<typeof ScheduleFormSchema>;

export interface ScheduleAppointmentDialogProps {
  leadId: string;
  leadName?: string;
  onClose: () => void;
  onScheduled?: (appointmentId: string) => void;
}

export function ScheduleAppointmentDialog({
  leadId,
  leadName,
  onClose,
  onScheduled,
}: ScheduleAppointmentDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const { agents } = useAssignableAgents();
  const agentOptions = agents.map((a) => ({ value: a.id, label: a.display_name }));

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(ScheduleFormSchema),
    defaultValues: {
      ...defaultTimeOrgTz(new Date()),
      durationMinutes: '30',
      assignedAgentId: '',
      channel: 'phone_manual',
      reason: '',
      notes: '',
    },
  });

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const { mutate, isPending } = useCreateAppointment({
    onSuccess: (appointment) => {
      setSubmitError(null);
      onScheduled?.(appointment.id);
      onClose();
    },
    onError: (error) => {
      setSubmitError(error.message || 'Não foi possível agendar. Tente novamente.');
    },
  });

  const handleEscape = React.useCallback(() => {
    if (isDirty && !isPending) return; // guarda de edição — não fecha silenciosamente
    if (!isPending) onClose();
  }, [isDirty, isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  function onSubmit(values: ScheduleFormValues): void {
    const scheduledAt = orgLocalToUtcIso(values.date, values.time);
    if (!scheduledAt) {
      setSubmitError('Data e hora inválidas.');
      return;
    }

    const body: AppointmentCreateBody = {
      leadId,
      scheduledAt,
      durationMinutes: Number(values.durationMinutes),
      channel: values.channel as AppointmentCreateBody['channel'],
      ...(values.assignedAgentId ? { assignedAgentId: values.assignedAgentId } : {}),
      ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
      ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
    };

    setSubmitError(null);
    mutate(body);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-appointment-title"
        className={cn(
          'w-full max-w-lg rounded-md flex flex-col overflow-hidden',
          'max-h-[min(90vh,760px)]',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-start justify-between px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <h2
              id="schedule-appointment-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Agendar retorno
            </h2>
            {leadName && <p className="font-sans text-sm text-ink-3">{leadName}</p>}
          </div>

          <button
            type="button"
            onClick={handleEscape}
            disabled={isPending}
            aria-label="Fechar diálogo de agendamento"
            className={cn(
              'p-2 rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
            )}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
          className="flex flex-col gap-5 px-6 py-5 overflow-y-auto"
        >
          <ScheduleAppointmentFields
            register={register}
            errors={errors}
            agentOptions={agentOptions}
            disabled={isPending}
            idPrefix="schedule-appointment"
          />

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Erro ao agendar</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1 shrink-0"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="submit"
              variant="primary"
              disabled={isPending}
              className="w-full justify-center"
            >
              {isPending ? 'Agendando…' : submitError ? 'Tentar novamente' : 'Agendar'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
